import { useContext } from "react";
import Swal from "sweetalert2";
import { Card, Button } from "react-bootstrap";
import { Notyf } from "notyf";
import UserContext from "../context/UserContext";
import { useNavigate } from "react-router-dom";

function ProductCard({ product }) {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const notyf = new Notyf();
  const { name, description, price } = product;

  // Format price to Philippine Peso
  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-PH", {
      style: "currency",
      currency: "PHP",
    }).format(price);
  };

  const handleAddToCart = (product) => {
    // Check if user is logged in
    if (!localStorage.getItem("token") || !user) {
      Swal.fire({
        icon: "warning",
        title: "Not Logged In",
        text: "Please log in to add items to your cart",
        confirmButtonText: "Login",
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/login");
        }
      });
      return;
    }
    // fetch req
    fetch(`${process.env.REACT_APP_API_BASE_URL}/cart/add-to-cart`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify({
        productId: product._id,
        quantity: 1,
        subtotal: product.price,
      }),
    })
      .then((response) => {
        console.log("Response:", response);
        if (response.ok) {
          notyf.success(`${product.name} added to cart successfully`);
        } else {
          notyf.error(`Failed to add ${product.name} to cart`);
        }
      })
      .catch((error) => {
        notyf.error(`Error adding ${product.name} to cart: ${error}`);
      });
  };

  return (
    <Card
      className="h-100 product-card"
      style={{
        borderRadius: "20px",
        overflow: "hidden",
        border: "none",
        backgroundColor: "#ffffff",
        boxShadow: "0 8px 30px rgba(0, 0, 0, 0.12)",
        transition: "all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)",
      }}
    >
      {/* Product Image Placeholder */}
      <div
        className="card-img-top d-flex align-items-center justify-content-center position-relative"
        style={{
          height: "220px",
          background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
        }}
      >
        {/* Decorative Pattern Overlay */}
        <div
          className="position-absolute w-100 h-100"
          style={{
            background:
              'url("data:image/svg+xml,<svg xmlns=\\"http://www.w3.org/2000/svg\\" viewBox=\\"0 0 100 100\\"><defs><pattern id=\\"productCardPattern\\" width=\\"25\\" height=\\"25\\" patternUnits=\\"userSpaceOnUse\\"><circle cx=\\"12.5\\" cy=\\"12.5\\" r=\\"1.5\\" fill=\\"white\\" opacity=\\"0.1\\"/><circle cx=\\"6\\" cy=\\"19\\" r=\\"0.8\\" fill=\\"white\\" opacity=\\"0.15\\"/><circle cx=\\"19\\" cy=\\"6\\" r=\\"0.8\\" fill=\\"white\\" opacity=\\"0.15\\"/></pattern></defs><rect width=\\"100\\" height=\\"100\\" fill=\\"url(%23productCardPattern)\\"/></svg>")',
            opacity: 0.4,
          }}
        ></div>

        {/* Fresh Product Badge */}
        <div
          className="position-absolute"
          style={{ top: "15px", left: "15px", zIndex: 10 }}
        ></div>

        {/* Product Icon */}
        <div className="text-white text-center position-relative z-index-1">
          <div
            className="mb-2"
            style={{
              background: "rgba(255, 255, 255, 0.2)",
              borderRadius: "50%",
              width: "80px",
              height: "80px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto",
              backdropFilter: "blur(10px)",
            }}
          >
            <i
              className="fas fa-leaf"
              style={{ fontSize: "2.5rem", opacity: 0.9 }}
            ></i>
          </div>
          <div style={{ fontSize: "0.85rem", opacity: 0.9, fontWeight: "500" }}>
            Premium Quality
          </div>
        </div>
      </div>

      <Card.Body className="d-flex flex-column p-4">
        {/* Product Name */}
        <Card.Title
          className="fw-bold text-dark mb-3"
          style={{
            fontSize: "1.2rem",
            minHeight: "2.4rem",
            lineHeight: "1.2",
          }}
        >
          {name}
        </Card.Title>

        {/* Product Description */}
        <Card.Text
          className="text-muted flex-grow-1 mb-4"
          style={{
            fontSize: "0.9rem",
            lineHeight: "1.5",
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {description}
        </Card.Text>

        {/* Price and Add to Cart Section */}
        <div className="mt-auto">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <span
                className="fw-bold text-success d-block"
                style={{ fontSize: "1.4rem" }}
              >
                {formatPrice(price)}
              </span>
              <small className="text-muted">per kg</small>
            </div>
            <div className="text-end"></div>
          </div>

          {/* Action Buttons */}
          <div className="d-grid gap-2">
            <Button
              onClick={() => handleAddToCart(product)}
              variant="success"
              size="sm"
              className="fw-semibold"
              style={{
                borderRadius: "12px",
                padding: "12px 20px",
                fontSize: "0.9rem",
                background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                border: "none",
                transition: "all 0.2s ease",
                boxShadow: "0 4px 15px rgba(40, 167, 69, 0.3)",
              }}
            >
              <i className="fas fa-shopping-cart me-2"></i>
              Add to Cart
            </Button>
          </div>
        </div>
      </Card.Body>

      {/* Enhanced Hover Effect Styles */}
      <style jsx>{`
        .product-card {
          transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
          cursor: pointer;
        }

        .product-card:hover {
          transform: translateY(-10px) scale(1.02);
          box-shadow: 0 25px 50px rgba(40, 167, 69, 0.15) !important;
        }

        .product-card .btn {
          transition: all 0.3s ease;
        }

        .product-card .btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(40, 167, 69, 0.4) !important;
          background: linear-gradient(
            135deg,
            #218838 0%,
            #1e7e34 100%
          ) !important;
        }

        .product-card:hover .badge {
          transform: scale(1.05);
          transition: transform 0.2s ease;
        }

        .product-card:hover .card-img-top {
          background: linear-gradient(
            135deg,
            #20c997 0%,
            #17a2b8 100%
          ) !important;
          transition: background 0.3s ease;
        }

        @media (max-width: 768px) {
          .product-card:hover {
            transform: translateY(-5px);
          }
        }

        @keyframes pulse {
          0% {
            box-shadow: 0 0 0 0 rgba(40, 167, 69, 0.4);
          }
          70% {
            box-shadow: 0 0 0 10px rgba(40, 167, 69, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(40, 167, 69, 0);
          }
        }

        .product-card .btn:active {
          animation: pulse 0.6s;
        }
      `}</style>
    </Card>
  );
}

export default ProductCard;
